import React, { useCallback, useContext, useId } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { Url, sanitizeHref } from '@wix/da-url';
import { PapiUser } from '@wix/da-papi-types';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import MainToggle from '@wix/da-ds/pkg/formControls/dsPresets/MainToggle';
import MultiselectField from '@wix/da-ds/pkg/Dropdown/multiselect/MultiselectField';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import InfoIcon from '@wix/da-ds/pkg/Icons/16x16/Info';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import ButtonFieldLayout from '../../layouts/common/ButtonFieldLayout';
import { useCoreMonetizationUpsellPopup } from '../../modals/CoreMonetizationUpsellPopup/useCoreMonetizationUpsellPopup';
import { SubmitFormContext } from '../../context';
import { useTierSelectField } from './useTierSelectField';

import s from './TierSelectField.scss';

interface Props {
  className?: string;
}

const TierSelectField: React.FC<Props> = ({ className }) => {
  const { t } = useTranslation();
  const toggleId = useId();
  const { initResponse } = useContext(SubmitFormContext)!;
  const { hasCoreMembership, user } = useContext(SessionContext);
  const { username } = user as PapiUser;

  // Non-core users can edit tiers if the deviation belongs to one already
  const hasTiers = (initResponse?.tiers?.length ?? 0) > 0;
  const inTiers = (initResponse?.inTiers?.length ?? 0) > 0;
  const canSubmitToTier = (hasCoreMembership && hasTiers) || inTiers;

  const { showCoreUpsellPopup, coreUpsellPopup } = useCoreMonetizationUpsellPopup();

  const handleToggleChange = useCallback(() => {
    if (!canSubmitToTier) {
      showCoreUpsellPopup();
      return;
    }
  }, [canSubmitToTier, showCoreUpsellPopup]);

  const { id, error, onChange, items, ref } = useTierSelectField();

  const showCreateButton = hasCoreMembership && !hasTiers;
  const showDropdown = canSubmitToTier && hasTiers;

  const toggle =
    showCreateButton || canSubmitToTier ? null : (
      <MainToggle
        variant="main"
        id={toggleId}
        size={'small'}
        value={false}
        onChange={handleToggleChange}
        aria-labelledby={`${toggleId}-label`}
      />
    );

  const bottomSlot = showDropdown && (
    <MultiselectField
      id={id}
      className={s['select-field']}
      label={t('deviationSubmit.form.tiers.select.label')}
      placeholder={t('deviationSubmit.form.tiers.select.placeholder')}
      error={error?.message}
      items={items}
      onChange={onChange}
      isLabelHidden
    />
  );

  return (
    <div
      className={cn(s['root'], className)}
      ref={el => {
        ref.current = el;
      }}
    >
      <ButtonFieldLayout
        label={
          <span className={s['label']}>
            {t('deviationSubmit.form.tiers.toggle.label')}
            <SimpleTooltip
              text={t('deviationSubmit.form.tiers.toggle.tooltip', {
                postProcess: ['reactPostprocessor'],
                learnMoreLink: (
                  <a
                    href={sanitizeHref(t('deviationSubmit.form.tiers.toggle.tooltip.url'))}
                    rel="license noopener noreferrer"
                    target="_blank"
                  >
                    {t('deviationSubmit.form.tiers.toggle.tooltip.link')}
                  </a>
                ),
              })}
              stopPropagation={true}
            >
              <InfoIcon
                className={s['tooltip-icon']}
                size={IconSize.SMALLER}
                disableDefaultColors
              />
            </SimpleTooltip>
          </span>
        }
        labelProps={{ id: `${toggleId}-label`, htmlFor: toggleId }}
        bottomSlot={bottomSlot}
      >
        {showCreateButton ? (
          <TextButton
            href={Url.userSubscriptionsLink({ username })}
            target="_blank"
            size="medium"
            variant="main"
          >
            {t('deviationSubmit.form.tiers.create.label')}
          </TextButton>
        ) : (
          toggle
        )}
      </ButtonFieldLayout>
      {coreUpsellPopup}
    </div>
  );
};
TierSelectField.displayName = 'TierSelectField';

export default TierSelectField;
